import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { PageShell } from "@/components/mai/PageShell";
import { useProduct } from "@/context/ProductContext";
import { toast } from "sonner";
import { Broadcast, Plus, CheckCircle, XCircle, CircleNotch, ArrowDown, Rss, Trash, ArrowsClockwise } from "@phosphor-icons/react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TYPE_LABELS = {
  hta_methodology: ["HTA Methodology", "#1D4ED8"], reimbursement_policy: ["Reimbursement Policy", "#047857"],
  regulatory_guidance: ["Regulatory Guidance", "#7C3AED"], evidence_requirement: ["Evidence Requirement", "#B45309"],
  submission_template: ["Submission Template", "#0E7490"], pricing_framework: ["Pricing Framework", "#BE185D"],
  competitive_outcome: ["Competitive Outcome", "#B91C1C"], scientific_publication: ["Scientific Publication", "#4338CA"],
  other: ["Update", "#64748B"],
};
const inputCls = "w-full text-xs border border-slate-300 px-2.5 py-2";

function LogUpdateDialog({ onClose, onCreated }) {
  const [f, setF] = useState({ title_hint: "", source_name: "", source_url: "", text: "" });
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/monitor/events/draft`, { ...f, source_name: f.source_name || "Manual update" });
      toast.success("Knowledge Event drafted — review its impact below");
      onCreated();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not draft event");
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="log-update-dialog" className="max-w-lg">
        <DialogHeader><DialogTitle className="font-heading text-sm">Log an Intelligence Update</DialogTitle></DialogHeader>
        <p className="text-[11px] text-slate-500 -mt-1">Paste an HTA/policy/regulatory update. The AI classifies it, maps affected countries, products and evidence, and drafts the Knowledge Event for your approval.</p>
        <div className="space-y-3">
          <input data-testid="update-title-input" className={inputCls} placeholder="Title hint (optional)" value={f.title_hint} onChange={(e) => setF({ ...f, title_hint: e.target.value })} />
          <div className="grid grid-cols-2 gap-3">
            <input className={inputCls} placeholder="Source name (e.g. NICE news)" value={f.source_name} onChange={(e) => setF({ ...f, source_name: e.target.value })} />
            <input className={inputCls} placeholder="Source URL (optional)" value={f.source_url} onChange={(e) => setF({ ...f, source_url: e.target.value })} />
          </div>
          <textarea data-testid="update-text-input" className={`${inputCls} min-h-[160px]`} placeholder="Paste the update text here…" value={f.text} onChange={(e) => setF({ ...f, text: e.target.value })} />
        </div>
        <button data-testid="update-submit-btn" disabled={busy || f.text.trim().length < 40} onClick={submit}
          className="w-full bg-slate-900 text-white text-xs font-semibold py-2.5 hover:bg-slate-800 disabled:opacity-50 flex items-center justify-center gap-2">
          {busy ? <><CircleNotch size={14} className="animate-spin" /> AI analysing impact…</> : "Draft Knowledge Event"}
        </button>
      </DialogContent>
    </Dialog>
  );
}

function SourcesDialog({ onClose }) {
  const [sources, setSources] = useState([]);
  const [presets, setPresets] = useState([]);
  const [showCatalog, setShowCatalog] = useState(false);
  const [f, setF] = useState({ name: "", url: "" });
  const [checking, setChecking] = useState(null);
  const [toggling, setToggling] = useState(null);
  const load = useCallback(() => Promise.all([
    axios.get(`${API}/monitor/sources`).then((r) => setSources(r.data)),
    axios.get(`${API}/monitor/sources/presets`).then((r) => setPresets(r.data)),
  ]), []);
  useEffect(() => { load(); }, [load]);
  const add = async (payload) => {
    try {
      await axios.post(`${API}/monitor/sources`, payload);
      setF({ name: "", url: "" });
      toast.success("Source registered — first check baselines the page, then changes are AI-drafted daily");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not add source");
    }
  };
  const toggle = async (s) => {
    setToggling(s.id);
    try { await axios.patch(`${API}/monitor/sources/${s.id}`, { active: !s.active }); await load(); }
    finally { setToggling(null); }
  };
  const check = async (id) => {
    setChecking(id);
    try {
      const r = await axios.post(`${API}/monitor/sources/${id}/check`);
      toast.success(r.data.baselined ? "Baseline captured — future changes will be AI-drafted into events"
        : r.data.changed ? `Change detected — ${r.data.events_drafted} event(s) drafted from new content`
        : "No change since last check");
    } catch {
      toast.error("Check failed");
    } finally {
      setChecking(null);
      load();
    }
  };
  const available = presets.filter((p) => !p.registered);
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="sources-dialog" className="max-w-2xl">
        <DialogHeader><DialogTitle className="font-heading text-sm">Monitored Source Registry</DialogTitle></DialogHeader>
        <p className="text-[11px] text-slate-500 -mt-1">Trusted HTA/policy pages checked automatically every 24h. The first check captures a baseline; only <span className="font-semibold">new content</span> is AI-drafted into Knowledge Events (up to 3 per change) for your review.</p>
        <button data-testid="toggle-catalog-btn" onClick={() => setShowCatalog(!showCatalog)}
          className="w-full text-left text-[11px] font-semibold text-slate-700 border border-slate-200 px-3 py-2 hover:bg-slate-50 flex items-center justify-between">
          <span>HTA Body Catalog — one-click registration ({available.length} available)</span>
          <span className="text-slate-400">{showCatalog ? "Hide" : "Show"}</span>
        </button>
        {showCatalog && (
          <div data-testid="preset-catalog" className="grid grid-cols-2 gap-1.5 max-h-[26vh] overflow-y-auto border border-slate-100 p-2">
            {presets.map((p) => (
              <button key={p.url} data-testid={`preset-${p.hta_body}`} disabled={p.registered}
                onClick={() => add({ name: p.name, url: p.url, country_hint: p.market })}
                className={`text-left text-[11px] px-2.5 py-2 border ${p.registered ? "border-emerald-200 bg-emerald-50/50 text-emerald-700 cursor-default" : "border-slate-200 hover:border-slate-900 text-slate-700"}`}>
                <span className="font-semibold block truncate">{p.name}</span>
                <span className="text-[10px] text-slate-400">{p.market}{p.registered ? " · registered ✓" : " · click to register"}</span>
              </button>
            ))}
          </div>
        )}
        <div className="grid grid-cols-5 gap-2">
          <input data-testid="source-name-input" className={`${inputCls} col-span-2`} placeholder="Custom name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} />
          <input data-testid="source-url-input" className={`${inputCls} col-span-2`} placeholder="https://…" value={f.url} onChange={(e) => setF({ ...f, url: e.target.value })} />
          <button data-testid="source-add-btn" disabled={!f.name || !f.url} onClick={() => add(f)}
            className="bg-slate-900 text-white text-xs font-semibold hover:bg-slate-800 disabled:opacity-50">Add</button>
        </div>
        <div className="max-h-[32vh] overflow-y-auto divide-y divide-slate-100 border border-slate-200">
          {sources.length === 0 && <p className="text-xs text-slate-400 text-center py-6">No sources yet — pick from the catalog above or add a custom URL.</p>}
          {sources.map((s) => (
            <div key={s.id} className={`px-3 py-2.5 flex items-center gap-3 ${s.active === false ? "opacity-50" : ""}`}>
              <span className={`w-2 h-2 rounded-full shrink-0 ${s.last_error ? "bg-red-500" : s.last_hash === undefined && !s.last_checked_at ? "bg-slate-300" : s.last_checked_at ? "bg-emerald-500" : "bg-slate-300"}`}
                title={s.last_error ? "Error" : s.last_checked_at ? "Healthy" : "Not checked yet"} />
              <div className="flex-1 min-w-0">
                <p className="text-xs font-semibold text-slate-800 truncate">{s.name}
                  {s.events_count > 0 && <span className="ml-2 font-mono-data text-[10px] text-emerald-700">{s.events_count} event{s.events_count > 1 ? "s" : ""}</span>}
                </p>
                <p className="font-mono-data text-[10px] text-slate-400 truncate">{s.url}</p>
                <p className="text-[10px] text-slate-400">
                  {s.last_error ? `⚠ ${s.last_error}` : s.last_checked_at ? `Checked ${new Date(s.last_checked_at).toLocaleString()}${s.last_changed_at ? ` · last change ${new Date(s.last_changed_at).toLocaleDateString()}` : " · baselined"}` : "Not checked yet"}
                </p>
              </div>
              <button data-testid={`source-toggle-${s.id}`} onClick={() => toggle(s)} title={s.active === false ? "Resume monitoring" : "Pause monitoring"}
                className="text-[10px] font-semibold text-slate-400 hover:text-slate-900 border border-slate-200 px-2 py-1.5">
                {s.active === false ? "Paused" : "Active"}
              </button>
              <button data-testid={`source-check-${s.id}`} onClick={() => check(s.id)} disabled={checking === s.id} title="Check now"
                className="text-slate-400 hover:text-slate-900 border border-slate-200 p-1.5">
                {checking === s.id ? <CircleNotch size={13} className="animate-spin" /> : <ArrowsClockwise size={13} />}
              </button>
              <button data-testid={`source-delete-${s.id}`} onClick={() => axios.delete(`${API}/monitor/sources/${s.id}`).then(load)} className="text-slate-300 hover:text-red-600 p-1"><Trash size={13} /></button>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function EventCard({ ev, onReview }) {
  const [selected, setSelected] = useState(() => (ev.proposed_updates || []).map((f) => f.id));
  const [busy, setBusy] = useState(false);
  const [tl, tc] = TYPE_LABELS[ev.event_type] || TYPE_LABELS.other;
  const act = async (verb) => {
    setBusy(true);
    try {
      await axios.post(`${API}/monitor/events/${ev.id}/${verb}`, verb === "approve" ? { apply_fact_ids: selected } : {});
      toast.success(verb === "approve" ? "Event approved — knowledge graph and readiness updated" : "Event dismissed");
      onReview();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Action failed");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div data-testid={`event-card-${ev.id}`} className={`bg-white border ${ev.status === "draft" ? "border-amber-300" : "border-slate-200"}`}>
      <div className="px-5 py-4">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-[9px] uppercase tracking-widest font-bold px-1.5 py-0.5 text-white" style={{ background: tc }}>{tl}</span>
          {ev.status === "draft" && <span className="text-[9px] uppercase tracking-widest font-bold px-1.5 py-0.5 bg-amber-600 text-white">Needs review</span>}
          {ev.status === "dismissed" && <span className="text-[9px] uppercase tracking-widest font-bold px-1.5 py-0.5 bg-slate-400 text-white">Dismissed</span>}
          <span className="font-mono-data text-[10px] text-slate-400">
            {ev.occurred_at || new Date(ev.created_at).toLocaleDateString()} · {ev.source?.name}{ev.source?.kind === "monitored" ? " (auto-monitored)" : ""}{Number.isFinite(ev.confidence) ? ` · confidence ${Math.round(ev.confidence * 100)}%` : ""}
          </span>
        </div>
        <p className="text-sm font-semibold text-slate-900 mt-2">{ev.title}</p>
        <p className="text-xs text-slate-500 leading-relaxed mt-1">{ev.summary}</p>
        <p className="text-[10px] text-slate-400 mt-1.5">
          {ev.countries?.length > 0 && <>Markets: <span className="font-semibold text-slate-600">{ev.countries.map((c) => c.name).join(", ")}</span></>}
          {ev.hta_bodies?.length > 0 && <> · HTA bodies: <span className="font-semibold text-slate-600">{ev.hta_bodies.join(", ")}</span></>}
        </p>
      </div>

      {ev.impacts?.length > 0 && (
        <div className="border-t border-slate-100 px-5 py-3 bg-slate-50/60">
          <p className="overline-label text-slate-400 flex items-center gap-1"><ArrowDown size={10} /> Impacts {ev.impacts.length} product{ev.impacts.length > 1 ? "s" : ""}</p>
          <div className="mt-2 space-y-2.5">
            {ev.impacts.map((im, i) => {
              const ri = (ev.readiness_impact || []).find((r) => r.product_id === im.product_id);
              return (
                <div key={i} className="text-xs">
                  <p className="font-semibold text-slate-800">
                    {im.product_name}
                    <span className="ml-2 font-normal text-slate-400">{(im.areas || []).join(" · ")}{im.gvd_update_needed && " · GVD update needed"}</span>
                    {ri && ri.gmari_before !== null && (
                      <span className={`ml-2 font-mono-data font-bold ${ri.delta > 0 ? "text-emerald-700" : ri.delta < 0 ? "text-red-700" : "text-slate-500"}`}>
                        GMARI {ri.gmari_before} → {ri.gmari_after}
                      </span>
                    )}
                  </p>
                  {im.affected_evidence?.length > 0 && <p className="text-[11px] text-slate-500 mt-0.5">Affected evidence: {im.affected_evidence.join(", ")}</p>}
                  <p className="text-[11px] text-slate-600 mt-0.5 border-l-2 border-slate-300 pl-2">→ {im.recommendation}</p>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {ev.status === "draft" && (
        <div className="border-t border-slate-100 px-5 py-3">
          {ev.proposed_updates?.length > 0 && (
            <>
              <p className="overline-label text-slate-400">Proposed knowledge graph updates (select to apply on approval)</p>
              <div className="mt-2 space-y-1.5">
                {ev.proposed_updates.map((f) => (
                  <label key={f.id} className="flex items-start gap-2 text-[11px] text-slate-600 cursor-pointer">
                    <input type="checkbox" data-testid={`event-fact-check-${f.id}`} className="mt-0.5" checked={selected.includes(f.id)}
                      onChange={(e) => setSelected(e.target.checked ? [...selected, f.id] : selected.filter((x) => x !== f.id))} />
                    <span><span className="font-semibold">{f.payload.country_name || f.payload.competitor || ""}</span> — {f.payload.detail || f.payload.outcome || ""} <span className="text-slate-400">({f.product_id})</span></span>
                  </label>
                ))}
              </div>
            </>
          )}
          <div className="flex items-center gap-2 mt-3">
            <button data-testid={`event-approve-${ev.id}`} disabled={busy} onClick={() => act("approve")}
              className="bg-emerald-700 text-white text-[11px] font-semibold px-4 py-2 hover:bg-emerald-800 flex items-center gap-1.5 disabled:opacity-50">
              {busy ? <CircleNotch size={12} className="animate-spin" /> : <CheckCircle size={13} weight="bold" />} Approve Event
            </button>
            <button data-testid={`event-dismiss-${ev.id}`} disabled={busy} onClick={() => act("dismiss")}
              className="text-red-600 border border-red-200 text-[11px] font-semibold px-4 py-2 hover:bg-red-50 flex items-center gap-1.5 disabled:opacity-50">
              <XCircle size={13} weight="bold" /> Dismiss
            </button>
          </div>
        </div>
      )}
      {ev.status === "approved" && ev.applied_updates?.length > 0 && (
        <div className="border-t border-slate-100 px-5 py-2.5">
          {ev.applied_updates.map((a, i) => <p key={i} className="text-[10px] text-emerald-700 font-semibold">✓ {a.applied}</p>)}
        </div>
      )}
    </div>
  );
}

export default function MonitorPage() {
  const { products } = useProduct();
  const [events, setEvents] = useState(null);
  const [filters, setFilters] = useState({ status: "", event_type: "", product_id: "" });
  const [dialog, setDialog] = useState(null);

  const load = useCallback(() => {
    const params = Object.fromEntries(Object.entries(filters).filter(([, v]) => v));
    axios.get(`${API}/monitor/events`, { params }).then((r) => setEvents(r.data)).catch(console.error);
  }, [filters]);
  useEffect(() => { load(); }, [load]);

  const sel = "text-[11px] border border-slate-200 px-2 py-1.5 bg-white text-slate-600";
  return (
    <PageShell pageId="monitor" overline="Global Intelligence Monitoring Engine" title="Intelligence Monitor"
      actions={
        <div className="flex items-center gap-2">
          <button data-testid="manage-sources-btn" onClick={() => setDialog("sources")}
            className="text-xs font-semibold text-slate-900 border border-slate-300 px-4 py-2.5 hover:bg-slate-50 flex items-center gap-2">
            <Rss size={14} /> Sources
          </button>
          <button data-testid="log-update-btn" onClick={() => setDialog("log")}
            className="bg-slate-900 text-white text-xs font-semibold px-4 py-2.5 hover:bg-slate-800 flex items-center gap-2">
            <Plus size={14} weight="bold" /> Log an Update
          </button>
        </div>
      }>
      <div className="flex items-center gap-2 flex-wrap">
        <select data-testid="filter-status" className={sel} value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })}>
          <option value="">All statuses</option><option value="draft">Needs review</option><option value="approved">Approved</option><option value="dismissed">Dismissed</option>
        </select>
        <select data-testid="filter-type" className={sel} value={filters.event_type} onChange={(e) => setFilters({ ...filters, event_type: e.target.value })}>
          <option value="">All types</option>
          {Object.entries(TYPE_LABELS).map(([k, [l]]) => <option key={k} value={k}>{l}</option>)}
        </select>
        <select data-testid="filter-product" className={sel} value={filters.product_id} onChange={(e) => setFilters({ ...filters, product_id: e.target.value })}>
          <option value="">All products</option>
          {products.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
      </div>

      {!events ? (
        <div className="py-24 text-center text-slate-400 text-sm">Loading timeline…</div>
      ) : events.length === 0 ? (
        <div data-testid="monitor-empty" className="bg-white border border-slate-200 py-16 text-center">
          <Broadcast size={32} className="text-slate-300 mx-auto" />
          <p className="font-heading font-bold text-sm text-slate-700 mt-3">The heartbeat starts here.</p>
          <p className="text-xs text-slate-400 mt-1 max-w-md mx-auto">Log your first intelligence update or register a monitored source — every change becomes a time-stamped Knowledge Event linked to markets, products, evidence and readiness.</p>
        </div>
      ) : (
        <div data-testid="events-timeline" className="space-y-4">
          {events.map((ev) => <EventCard key={ev.id} ev={ev} onReview={load} />)}
        </div>
      )}
      {dialog === "log" && <LogUpdateDialog onClose={() => setDialog(null)} onCreated={() => { setDialog(null); load(); }} />}
      {dialog === "sources" && <SourcesDialog onClose={() => setDialog(null)} />}
    </PageShell>
  );
}
